import { Html5Qrcode } from "html5-qrcode";

/**
 * Camera helpers for the end-of-day photos (V1_SPEC D29, D30, 11.4 A3): downscale to a JPEG no wider
 * or taller than 2000 px, a blur check, the sheet's QR read from the photo, and the torch. All on the
 * phone; no AI, nothing leaves the device until the upload.
 */

export const MAX_EDGE = 2000;
export const JPEG_QUALITY = 0.82;
/** Variance of the Laplacian below this = blurry. A guess until the pilot's real photos (V1_SPEC risk 7). */
export const BLUR_MIN = 60;
const BLUR_EDGE = 400;

/** Width and height that fit inside `max` on the long edge, keeping the shape (never enlarges). */
export const fitWithin = (w: number, h: number, max = MAX_EDGE): { width: number; height: number } => {
  const long = Math.max(w, h);
  if (long <= max || long === 0) return { width: Math.round(w), height: Math.round(h) };
  const k = max / long;
  return { width: Math.round(w * k), height: Math.round(h * k) };
};

/**
 * Variance of the 4-neighbour Laplacian over a greyscale image (one byte per pixel). Sharp text has
 * strong edges and a high variance; a shaken or out-of-focus photo is smooth and scores low.
 */
export const laplacianVariance = (gray: ArrayLike<number>, w: number, h: number): number => {
  if (w < 3 || h < 3) return 0;
  let sum = 0;
  let sumSq = 0;
  let n = 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const v = gray[i - w] + gray[i + w] + gray[i - 1] + gray[i + 1] - 4 * gray[i];
      sum += v;
      sumSq += v * v;
      n++;
    }
  }
  const mean = sum / n;
  return sumSq / n - mean * mean;
};

export const isBlurry = (score: number) => score < BLUR_MIN;

const loadBitmap = async (blob: Blob): Promise<{ draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void; width: number; height: number; close: () => void }> => {
  if (typeof createImageBitmap === "function") {
    const bmp = await createImageBitmap(blob);
    return { draw: (ctx, w, h) => ctx.drawImage(bmp, 0, 0, w, h), width: bmp.width, height: bmp.height, close: () => bmp.close() };
  }
  const url = URL.createObjectURL(blob);
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("IMAGE_UNREADABLE"));
    i.src = url;
  });
  return { draw: (ctx, w, h) => ctx.drawImage(img, 0, 0, w, h), width: img.naturalWidth, height: img.naturalHeight, close: () => URL.revokeObjectURL(url) };
};

const canvasOf = (w: number, h: number) => {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d");
  if (!ctx) throw new Error("CANVAS_UNAVAILABLE");
  return { c, ctx };
};

export type PreparedPhoto = { blob: Blob; width: number; height: number; blurScore: number };

/** Downscale to ≤ 2000 px JPEG q 0.82 and score sharpness on a 400 px greyscale copy. */
export const preparePhoto = async (file: Blob): Promise<PreparedPhoto> => {
  const src = await loadBitmap(file);
  try {
    const size = fitWithin(src.width, src.height, MAX_EDGE);
    const { c, ctx } = canvasOf(size.width, size.height);
    src.draw(ctx, size.width, size.height);
    const blob = await new Promise<Blob>((resolve, reject) =>
      c.toBlob((b) => (b ? resolve(b) : reject(new Error("ENCODE_FAILED"))), "image/jpeg", JPEG_QUALITY)
    );
    const small = fitWithin(size.width, size.height, BLUR_EDGE);
    const { ctx: sctx } = canvasOf(small.width, small.height);
    sctx.drawImage(c, 0, 0, small.width, small.height);
    const rgba = sctx.getImageData(0, 0, small.width, small.height).data;
    const gray = new Uint8ClampedArray(small.width * small.height);
    for (let i = 0, j = 0; j < gray.length; i += 4, j++) gray[j] = (rgba[i] * 299 + rgba[i + 1] * 587 + rgba[i + 2] * 114) / 1000;
    return { blob, width: size.width, height: size.height, blurScore: laplacianVariance(gray, small.width, small.height) };
  } finally {
    src.close();
  }
};

/** What a printed page's QR says (`HKPCB1|printId|sheetNo|revision|page|pages|rowsOnPage`, V1_SPEC 7.1). */
export type SheetQr = { printId: string; sheetNo: string; revision: number; page: number; pages: number; rowsOnPage: number; text: string };

export const parseSheetQr = (text: string | null | undefined): SheetQr | null => {
  const parts = (text || "").trim().split("|");
  if (parts.length !== 7 || parts[0] !== "HKPCB1") return null;
  const [, printId, sheetNo, rev, page, pages, rows] = parts;
  const nums = [rev, page, pages, rows].map((x) => (/^\d+$/.test(x) ? Number(x) : NaN));
  if (!printId || !sheetNo || nums.some((x) => !isFinite(x)) || nums[1] < 1 || nums[1] > nums[2]) return null;
  return { printId, sheetNo, revision: nums[0], page: nums[1], pages: nums[2], rowsOnPage: nums[3], text: text!.trim() };
};

/** Read the sheet's QR from the photo (html5-qrcode scanFile needs a mounted element id). Null when none is found. */
export const readSheetQr = async (file: Blob, elementId: string): Promise<SheetQr | null> => {
  try {
    const scanner = new Html5Qrcode(elementId, { verbose: false } as any);
    const f = file instanceof File ? file : new File([file], "sheet.jpg", { type: file.type || "image/jpeg" });
    try {
      return parseSheetQr(await scanner.scanFile(f, false));
    } finally {
      try {
        scanner.clear();
      } catch {
        // Nothing drawn: nothing to clear.
      }
    }
  } catch {
    return null;
  }
};

/** The phone can switch the light on for this camera track. */
export const torchSupported = (track?: MediaStreamTrack | null): boolean => {
  try {
    return !!(track && typeof (track as any).getCapabilities === "function" && (track as any).getCapabilities()?.torch);
  } catch {
    return false;
  }
};

export const setTorch = async (track: MediaStreamTrack, on: boolean): Promise<boolean> => {
  try {
    await track.applyConstraints({ advanced: [{ torch: on } as any] });
    return true;
  } catch {
    return false;
  }
};

/** One still frame from the live video, as a full-size JPEG (downscaled later with the rest). */
export const grabFrame = async (video: HTMLVideoElement): Promise<Blob> => {
  const { c, ctx } = canvasOf(video.videoWidth, video.videoHeight);
  ctx.drawImage(video, 0, 0);
  return new Promise<Blob>((resolve, reject) => c.toBlob((b) => (b ? resolve(b) : reject(new Error("ENCODE_FAILED"))), "image/jpeg", 0.92));
};
